import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  LOAD_TRACKING_MONTH_ERROR,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  MONTH_SHORT,
  currentTrackingYear,
  monthLabelFromKey,
  trackingCellDisplay,
} from "@/modules/despacho/complianceTrackingHelpers";
import { syncWorkClientFromDespacho } from "@/lib/clientSelection";
import { useAuth } from "@/lib/auth";
import { activeStaffForAssignment, isDespachoAssignmentScopedUser } from "@/modules/despacho/despachoAssignmentsHelpers";
import {
  FISCAL_BOARD_STAFF_FILTER_ALL,
  isFiscalBoardStaffFilterAll,
} from "@/modules/despacho/despachoFiscalBoardHelpers";
import { despachoStaffApi } from "@/modules/despacho/despachoStaffApi";
import { despachoRoleLabel } from "@/modules/despacho/despachoStaffHelpers";
import TrackingMonthCapturePanel from "@/pages/despacho/TrackingMonthCapturePanel";
import ComplianceInstanceDetailDialog from "@/pages/despacho/ComplianceInstanceDetailDialog";

function TrackingCell({ status, onClick, title, interactive = false, projected = false }) {
  const { symbol, className, label } = trackingCellDisplay(status, { projected });
  const clickable = Boolean(onClick);
  return (
    <button
      type="button"
      className={`inline-flex h-7 w-7 items-center justify-center rounded-sm border text-sm ${
        clickable
          ? "border-slate-200 hover:bg-slate-100 hover:border-slate-300 cursor-pointer"
          : "border-slate-100 cursor-default opacity-70"
      } ${className}`}
      title={title || label}
      onClick={onClick}
      disabled={!clickable}
      data-testid={interactive ? "despacho-tracking-cell-interactive" : undefined}
    >
      {symbol}
    </button>
  );
}

export default function FiscalTrackingPanel({
  readOnly = false,
  onInstancesChanged,
  trackingDeepLink = null,
  onTrackingDeepLinkConsumed,
}) {
  const { user } = useAuth();
  const scopedStaffUser = isDespachoAssignmentScopedUser(user);
  const [year, setYear] = useState(currentTrackingYear());
  const [selectedAuxId, setSelectedAuxId] = useState(
    scopedStaffUser ? String(user?.id || "") : FISCAL_BOARD_STAFF_FILTER_ALL,
  );
  const [staffOptions, setStaffOptions] = useState([]);
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState("idle");
  const [loadMessage, setLoadMessage] = useState("");
  const [priorOpen, setPriorOpen] = useState(false);
  const [priorItems, setPriorItems] = useState([]);
  const [priorCompany, setPriorCompany] = useState(null);
  const [priorLoading, setPriorLoading] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [monthDetail, setMonthDetail] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthGenerating, setMonthGenerating] = useState(false);
  const [monthBackgroundSync, setMonthBackgroundSync] = useState(false);
  const [instanceDetailId, setInstanceDetailId] = useState(null);
  const [instanceDetailOpen, setInstanceDetailOpen] = useState(false);
  const deepLinkConsumedRef = useRef(false);
  const pendingDeepLinkRef = useRef(null);

  const yearOptions = useMemo(() => {
    const y = currentTrackingYear();
    return [y - 1, y, y + 1];
  }, []);

  const assignedAuxiliarId = useMemo(
    () => (isFiscalBoardStaffFilterAll(selectedAuxId) ? undefined : selectedAuxId),
    [selectedAuxId],
  );

  const viewingAllAuxiliaries = isFiscalBoardStaffFilterAll(selectedAuxId);

  useEffect(() => {
    let cancelled = false;
    despachoStaffApi
      .list()
      .then((staff) => {
        if (!cancelled) setStaffOptions(activeStaffForAssignment(staff));
      })
      .catch(() => {
        if (!cancelled) setStaffOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const auxSelectOptions = useMemo(() => {
    if (staffOptions.length) {
      return staffOptions.map((s) => ({
        id: s.id,
        label: `${s.name || s.email || s.id} · ${despachoRoleLabel(s.despacho_role)}`,
      }));
    }
    return (data?.auxiliaries || []).map((row) => ({
      id: row.assigned_to_user_id,
      label: row.auxiliar_name || row.assigned_to_user_id,
    }));
  }, [staffOptions, data?.auxiliaries]);

  const loadMatrix = useCallback(async () => {
    setLoadState("loading");
    setLoadMessage("");
    try {
      const payload = await despachoFiscalInfoApi.getTrackingMatrix({
        year,
        assignedAuxiliarId,
      });
      setData(payload);
      setLoadState("ready");
    } catch (err) {
      setData(null);
      const parsed = parseFiscalInfoError(err);
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parsed.message);
    }
  }, [year, assignedAuxiliarId]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const openPriorPeriods = async (company) => {
    syncWorkClientFromDespacho(company?.fiscal_client_id);
    setPriorCompany(company);
    setPriorOpen(true);
    setPriorLoading(true);
    try {
      const res = await despachoFiscalInfoApi.getTrackingPriorPeriods({
        year,
        fiscalClientId: company.fiscal_client_id,
        assignedAuxiliarId,
      });
      setPriorItems(res.items || []);
    } catch (err) {
      setPriorItems([]);
    } finally {
      setPriorLoading(false);
    }
  };

  const fetchMonthDetail = useCallback(async (company, monthIndex, { autoGenerate = true } = {}) => {
    return despachoFiscalInfoApi.getTrackingMonthDetail({
      year,
      month: monthIndex,
      fiscalClientId: company.fiscal_client_id,
      assignedAuxiliarId,
      autoGenerate,
    });
  }, [year, assignedAuxiliarId]);

  const applyMonthDetailPayload = useCallback((detail, company) => {
    const payload = { ...detail, company };
    setMonthDetail(payload);
    const prep = detail?.generation?.obligations_prep;
    const csfAdded = Number(prep?.csf_added || 0);
    const cfdiAssigned = Number(prep?.cfdi_assigned || 0);
    if (csfAdded > 0 || cfdiAssigned > 0) {
      const parts = [];
      if (csfAdded > 0) parts.push(`${csfAdded} desde constancia fiscal`);
      if (cfdiAssigned > 0) parts.push(`${cfdiAssigned} detectadas por CFDI`);
      toast.message(`Se activaron obligaciones: ${parts.join(" · ")}.`);
    }
    if (detail?.generation?.instances_created > 0) {
      toast.message(`Se generaron ${detail.generation.instances_created} vencimiento(s) del mes.`);
      loadMatrix();
    }
    if (detail?.generation?.error) {
      toast.error(String(detail.generation.error).slice(0, 200));
    }
    return payload;
  }, [loadMatrix]);

  const openMonthDetail = async (company, monthIndex, { autoGenerate = true } = {}) => {
    syncWorkClientFromDespacho(company?.fiscal_client_id);
    setMonthLoading(true);
    setMonthOpen(true);
    setMonthDetail(null);
    setMonthBackgroundSync(false);
    try {
      if (autoGenerate) {
        const quick = await fetchMonthDetail(company, monthIndex, { autoGenerate: false });
        applyMonthDetailPayload(quick, company);
        setMonthLoading(false);
        setMonthBackgroundSync(true);
        try {
          const full = await fetchMonthDetail(company, monthIndex, { autoGenerate: true });
          applyMonthDetailPayload(full, company);
        } catch (err) {
          const message = parseFiscalInfoError(err, { fallbackMessage: LOAD_TRACKING_MONTH_ERROR }).message;
          if ((quick?.groups || []).length) {
            toast.error(message);
          } else {
            setMonthDetail((prev) => ({
              ...(prev || {}),
              company,
              error: message,
            }));
          }
        } finally {
          setMonthBackgroundSync(false);
        }
        return;
      }
      const detail = await fetchMonthDetail(company, monthIndex, { autoGenerate: false });
      applyMonthDetailPayload(detail, company);
    } catch (err) {
      setMonthDetail({
        error: parseFiscalInfoError(err, { fallbackMessage: LOAD_TRACKING_MONTH_ERROR }).message,
        company,
      });
    } finally {
      setMonthLoading(false);
      setMonthBackgroundSync(false);
    }
  };

  const reloadMonthDetail = useCallback(async () => {
    if (!monthDetail?.company || !monthDetail?.month) return;
    try {
      const detail = await fetchMonthDetail(monthDetail.company, monthDetail.month, { autoGenerate: true });
      applyMonthDetailPayload(detail, monthDetail.company);
      onInstancesChanged?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err, { fallbackMessage: LOAD_TRACKING_MONTH_ERROR }).message);
    }
  }, [monthDetail, fetchMonthDetail, applyMonthDetailPayload, onInstancesChanged]);

  const handleGenerateMonthInstances = async () => {
    if (readOnly || !monthDetail?.company || !monthDetail?.period_key) return;
    setMonthGenerating(true);
    try {
      await openMonthDetail(monthDetail.company, monthDetail.month, { autoGenerate: true });
    } finally {
      setMonthGenerating(false);
    }
  };

  useEffect(() => {
    deepLinkConsumedRef.current = false;
    pendingDeepLinkRef.current = trackingDeepLink;
  }, [trackingDeepLink?.clientId, trackingDeepLink?.periodKey, trackingDeepLink?.openMonth]);

  useEffect(() => {
    const link = pendingDeepLinkRef.current;
    if (!link?.openMonth || !link.clientId || !link.periodKey || deepLinkConsumedRef.current) return;
    const periodMatch = String(link.periodKey).match(/^(\d{4})-(\d{2})$/);
    if (!periodMatch) return;
    const linkYear = Number(periodMatch[1]);
    const monthIndex = Number(periodMatch[2]);
    if (!Number.isFinite(linkYear) || monthIndex < 1 || monthIndex > 12) return;
    if (linkYear !== year) {
      setYear(linkYear);
      return;
    }
    if (loadState !== "ready" || !data?.matrix?.companies?.length) return;
    const company = data.matrix.companies.find((c) => c.fiscal_client_id === link.clientId);
    deepLinkConsumedRef.current = true;
    pendingDeepLinkRef.current = null;
    if (company) {
      openMonthDetail(company, monthIndex, { autoGenerate: true });
    }
    onTrackingDeepLinkConsumed?.();
  }, [year, loadState, data, trackingDeepLink, onTrackingDeepLinkConsumed]);

  const auxColumns = useMemo(
    () => [
      {
        key: "auxiliar_name",
        label: "Auxiliar",
        render: (r) => (
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-sm font-medium"
            onClick={() => setSelectedAuxId(r.assigned_to_user_id)}
          >
            {r.auxiliar_name}
          </Button>
        ),
      },
      { key: "companies_assigned", label: "Empresas asignadas" },
      { key: "companies_with_pending", label: "Con pendientes" },
      { key: "overdue_count", label: "Vencidas" },
      { key: "due_soon_count", label: "Por vencer" },
      {
        key: "progress_percent",
        label: "Avance",
        render: (r) => `${r.progress_percent ?? 0}%`,
      },
    ],
    [],
  );

  const monthColumns = data?.month_columns || [];

  return (
    <div className="space-y-4" data-testid="despacho-fiscal-tracking-panel">
      <DespachoSectionCard
        title="Seguimiento de tareas por auxiliar"
        description="Matriz empresa × mes. Los meses pendientes se marcan automáticamente; al hacer clic en un mes se generan vencimientos y puede capturar declaración e impuesto."
      >
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-xs text-slate-600">Año</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="rounded-sm h-8 w-28 text-sm mt-0.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[220px]">
            <Label htmlFor="despacho-tracking-aux-filter" className="text-xs text-slate-600">
              Auxiliar contable
            </Label>
            <Select
              value={selectedAuxId || FISCAL_BOARD_STAFF_FILTER_ALL}
              onValueChange={setSelectedAuxId}
              disabled={scopedStaffUser}
            >
              <SelectTrigger
                id="despacho-tracking-aux-filter"
                className="rounded-sm h-8 text-sm mt-0.5 bg-white"
                data-testid="despacho-tracking-aux-filter"
              >
                <SelectValue placeholder="Todos los auxiliares" />
              </SelectTrigger>
              <SelectContent>
                {!scopedStaffUser ? (
                  <SelectItem
                    value={FISCAL_BOARD_STAFF_FILTER_ALL}
                    data-testid="despacho-tracking-aux-filter-all"
                  >
                    Todos los auxiliares
                  </SelectItem>
                ) : null}
                {auxSelectOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loadState === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando seguimiento…
          </div>
        ) : null}

        {loadState === "error" || loadState === "forbidden" ? (
          <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <p className="m-0">{loadMessage}</p>
          </div>
        ) : null}

        {loadState === "ready" && data && viewingAllAuxiliaries ? (
          <DespachoSimpleTable
            columns={auxColumns}
            rows={(data.auxiliaries || []).map((r) => ({ ...r, id: r.assigned_to_user_id }))}
            emptyLabel="Sin auxiliares con empresas asignadas. Asigne empresas en Clientes → Asignación."
          />
        ) : null}

        {loadState === "ready" && data?.matrix ? (
          <div className="space-y-2" data-testid="despacho-tracking-matrix">
            <p className="text-sm font-medium text-[#0F2D5C] m-0">
              {data.matrix.auxiliar_name}
              <span className="text-xs font-normal text-slate-500 ml-2">
                {data.matrix.companies?.length || 0} empresa(s) · {year}
              </span>
            </p>
            <div className="overflow-x-auto border border-slate-200 rounded-sm bg-white">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-2 font-medium sticky left-0 bg-slate-50 z-10 min-w-[180px]">Empresa</th>
                    <th className="p-2 font-medium text-center min-w-[72px]">Ant.</th>
                    {monthColumns.map((mk) => (
                      <th key={mk} className="p-2 font-medium text-center min-w-[40px]">
                        {monthLabelFromKey(mk)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.matrix.companies || []).map((company) => (
                    <tr key={company.fiscal_client_id} className="border-b border-slate-100">
                      <td className="p-2 sticky left-0 bg-white z-10">
                        <button
                          type="button"
                          className="text-left w-full hover:bg-slate-50 rounded-sm -m-1 p-1"
                          title="Activar esta empresa en la barra superior"
                          onClick={() => syncWorkClientFromDespacho(company.fiscal_client_id)}
                        >
                          <div className="font-medium text-slate-900 line-clamp-2">{company.client_name}</div>
                          {company.client_rfc ? (
                            <div className="text-[10px] text-slate-500">{company.client_rfc}</div>
                          ) : null}
                          {company.fecha_inicio_control ? (
                            <div className="text-[10px] text-slate-400" data-testid="matrix-control-start">
                              Control desde {(company.fecha_inicio_control || "").slice(0, 7)}
                            </div>
                          ) : null}
                        </button>
                      </td>
                      <td className="p-2 text-center">
                        <TrackingCell
                          status={company.prior_periods_status}
                          title={`Periodos anteriores (${company.prior_periods_count || 0})`}
                          onClick={
                            company.prior_periods_count
                              ? () => openPriorPeriods(company)
                              : undefined
                          }
                        />
                      </td>
                      {monthColumns.map((mk, idx) => {
                        const st = company.months?.[mk] || "empty";
                        const meta = company.months_meta?.[mk];
                        const projected = Boolean(meta?.projected);
                        const beforeControl = Boolean(meta?.before_control_start);
                        return (
                          <td key={mk} className="p-1 text-center">
                            <TrackingCell
                              status={st}
                              projected={projected}
                              interactive
                              title={`${monthLabelFromKey(mk)} ${year}${
                                beforeControl
                                  ? " · anterior al inicio de control (no aplica)"
                                  : projected
                                  ? " · proyectado (sin vencimientos generados)"
                                  : meta?.instance_count
                                    ? ` · ${meta.instance_count} obl.${
                                      meta.open_count
                                        ? ` · ${meta.open_count} pendiente${meta.open_count === 1 ? "" : "s"}`
                                        : " · cubierto"
                                    }`
                                    : " · clic para capturar"
                              }`}
                              onClick={() => openMonthDetail(company, idx + 1)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-3 text-[10px] text-slate-600 pt-1">
              <span>✅ Cerrado</span>
              <span>🔒 Con evidencia</span>
              <span>❌ Pendiente / vencido</span>
              <span className="border border-dashed border-rose-300/80 px-1 rounded-sm">❌ Proyectado (sin vencimientos)</span>
              <span>⏳ En proceso</span>
              <span>⚠️ Por vencer</span>
              <span>— No aplica</span>
              <span>○ Sin instancias</span>
            </div>
          </div>
        ) : null}
      </DespachoSectionCard>

      <Dialog open={priorOpen} onOpenChange={setPriorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Periodos anteriores</DialogTitle>
            <DialogDescription>
              {priorCompany?.client_name || "Empresa"} — obligaciones abiertas antes de {year}
            </DialogDescription>
          </DialogHeader>
          {priorLoading ? (
            <div className="flex items-center gap-2 text-sm py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando…
            </div>
          ) : (
            <DespachoSimpleTable
              columns={[
                { key: "period_key", label: "Periodo" },
                { key: "obligation_name", label: "Obligación" },
                {
                  key: "cell_status_label",
                  label: "Estado",
                  render: (r) => (
                    <DespachoStatusBadge variant="secondary">{r.cell_status_label || r.status_label}</DespachoStatusBadge>
                  ),
                },
                {
                  key: "_open",
                  label: "",
                  render: (r) => (
                    <Button type="button" variant="link" size="sm" className="h-7 px-0" onClick={() => {
                      setInstanceDetailId(r.id);
                      setInstanceDetailOpen(true);
                    }}>
                      Detalle
                    </Button>
                  ),
                },
              ]}
              rows={priorItems}
              emptyLabel="Sin pendientes de periodos anteriores."
            />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPriorOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={monthOpen} onOpenChange={setMonthOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {monthDetail?.client_name || monthDetail?.company?.client_name || "Captura del mes"}
            </DialogTitle>
            <DialogDescription>
              {monthDetail?.period_key
                ? `Periodo ${monthDetail.period_key} — marque declaración, pago y evidencia por obligación`
                : "Cargando…"}
            </DialogDescription>
          </DialogHeader>
          {monthLoading ? (
            <div className="flex items-center gap-2 text-sm py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando obligaciones…
            </div>
          ) : null}
          {monthBackgroundSync ? (
            <div className="flex items-center gap-2 text-xs text-slate-600 py-2 px-3 rounded-sm border border-slate-200 bg-slate-50">
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
              Sincronizando obligaciones desde constancia fiscal y generando vencimientos del mes…
            </div>
          ) : null}
          {!monthLoading && !monthBackgroundSync && monthDetail?.empty_hint && (monthDetail.groups || []).length ? (
            <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
              {monthDetail.empty_hint}
              {!readOnly ? (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 ml-2 text-xs text-amber-950 underline"
                  onClick={handleGenerateMonthInstances}
                  disabled={monthGenerating}
                >
                  Sincronizar de nuevo
                </Button>
              ) : null}
            </div>
          ) : null}
          {monthDetail?.error ? (
            <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 space-y-2">
              <p className="m-0">{monthDetail.error}</p>
              {!readOnly && monthDetail?.company ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8 text-xs"
                  onClick={handleGenerateMonthInstances}
                  disabled={monthGenerating}
                >
                  Reintentar
                </Button>
              ) : null}
            </div>
          ) : null}
          {!monthLoading && !monthBackgroundSync && !monthDetail?.error && monthDetail && !(monthDetail.groups || []).length ? (
            <div className="rounded-sm border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 space-y-3">
              <p className="m-0">
                {monthDetail.empty_hint || "No hay vencimientos generados para este mes."}
              </p>
              {monthDetail.obligations_summary?.applicable_for_period > 0 &&
              monthDetail.generation?.instances_planned === 0 &&
              (monthDetail.generation?.instances_duplicates > 0 ||
                monthDetail.generation?.plan_summary?.skipped_no_rule_count > 0) ? (
                <p className="m-0 text-xs text-slate-500">
                  {monthDetail.generation?.instances_duplicates > 0
                    ? `Duplicados detectados: ${monthDetail.generation.instances_duplicates}. `
                    : ""}
                  {monthDetail.generation?.plan_summary?.skipped_no_rule_count > 0
                    ? `Sin regla auto-generable: ${monthDetail.generation.plan_summary.skipped_no_rule_count}.`
                    : ""}
                </p>
              ) : null}
              {monthDetail.auto_generate_allowed === false ? (
                <p className="m-0 text-xs text-amber-800">
                  Su usuario no tiene permiso de escritura para generar vencimientos en este módulo.
                </p>
              ) : null}
              {!readOnly ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8 text-xs"
                  onClick={handleGenerateMonthInstances}
                  disabled={monthGenerating}
                  data-testid="despacho-tracking-generate-month"
                >
                  {monthGenerating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                  )}
                  Generar vencimientos del mes
                </Button>
              ) : null}
            </div>
          ) : null}
          {monthDetail?.fiscal_client_id && monthDetail?.period_key ? (
            <TrackingMonthCapturePanel
              groups={monthDetail.groups || []}
              periodKey={monthDetail.period_key}
              fiscalClientId={monthDetail.fiscal_client_id}
              clientName={monthDetail.client_name || ""}
              clientEmails={monthDetail.client_emails || []}
              monthHonorarios={monthDetail.month_honorarios || null}
              monthClientDocuments={monthDetail.month_client_documents || null}
              monthContableChecklist={monthDetail.month_contable_checklist || null}
              applicabilityHints={monthDetail.applicability_hints || {}}
              readOnly={readOnly}
              onUpdated={reloadMonthDetail}
            />
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMonthOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ComplianceInstanceDetailDialog
        instanceId={instanceDetailId}
        open={instanceDetailOpen}
        onOpenChange={setInstanceDetailOpen}
        readOnly={readOnly}
        onUpdated={() => {
          onInstancesChanged?.();
          loadMatrix();
        }}
      />
    </div>
  );
}
